const _excluded = ["type", "value", "initialValue", "onChange", "onFilter", "placeholder", "className", "inputClassName", "inputSize", "inputStyle", "options", "dataTestId", "dropdownStyle", "showPlusButton", "style"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React, { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash.debounce';
import { Form } from 'react-bootstrap';
import Dropdown from '../Dropdown';
import { Button, DeleteButton } from '../buttons';
import { multiChoiceFilterValuesFrom, serializeMultiChoiceFilterValues } from './multiChoiceFilterUtils';
const ADD_BUTTON_SIZE = 20;
const ADD_BUTTON_LEFT_SPACING = 5;
const ADD_BUTTON_INPUT_GAP = 8;
const LEFT_BORDER_WIDTH = 1;
const REMOVE_BUTTON_SLOT_WIDTH = 32;
const FILTER_CONTROL_MIN_WIDTH = '200px';
const SUPPORTED_TYPES = ['text', 'select'];
const SELECT_ALL_OPTION = {
  key: 'all',
  value: '',
  text: 'all'
};
const isDE = process.env.PUBLIC_URL === '/de';
const SHOULD_DEFINE_FILTER_MIN_WIDTH = !isDE;
export const MULTI_CHOICE_FILTER_INPUT_OFFSET = "".concat(LEFT_BORDER_WIDTH + ADD_BUTTON_LEFT_SPACING + ADD_BUTTON_SIZE + ADD_BUTTON_INPUT_GAP, "px");
export const MULTI_CHOICE_FILTER_CONTROL_MIN_WIDTH = FILTER_CONTROL_MIN_WIDTH;

/**
 * Makes sure there is always at least one empty string - for which we render an input field.
 * Otherwise we would render no input fields for that filter.
 * @param {*} value
 * @returns
 */
const inputValuesFrom = value => {
  const inputValues = multiChoiceFilterValuesFrom(value);

  // Empty filter values still need one blank row so the component renders an input.
  return inputValues.length > 0 ? inputValues : [''];
};
const optionValue = option => typeof option === 'object' && option !== null ? option.value : option;
const normalizeSelectOption = option => typeof option === 'object' && option !== null ? option : {
  key: option,
  value: option,
  text: option
};
const selectOptionsWithAllOption = options => [SELECT_ALL_OPTION, ...(options || []).filter(option => optionValue(option) !== '').map(normalizeSelectOption)];
export default function MultiChoiceFilter(_ref) {
  let {
      type = 'text',
      value,
      initialValue = '',
      onChange,
      onFilter,
      placeholder = 'Filter',
      className = '',
      inputClassName = '',
      inputSize,
      inputStyle,
      options = [],
      dataTestId,
      dropdownStyle = {
        width: '100%'
      },
      showPlusButton = true,
      style
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  if (!SUPPORTED_TYPES.includes(type)) {
    throw new Error("Unsupported MultiChoiceFilter type: ".concat(type));
  }
  const filterValue = value === undefined ? initialValue : value;
  const [inputValues, setInputValues] = useState(inputValuesFrom(filterValue));
  const shouldShowLeftBorder = inputValues.length > 1;
  const plusButtonLeftSpacing = showPlusButton ? ADD_BUTTON_LEFT_SPACING : 0;
  const plusButtonInputGap = showPlusButton ? ADD_BUTTON_INPUT_GAP : 0;
  const plusButtonColumnWidth = showPlusButton ? ADD_BUTTON_SIZE : 0;
  const debouncedOnFilter = useMemo(() => {
    if (!onFilter) return null;
    return debounce(nextValue => onFilter(nextValue), 500);
  }, [onFilter]);
  useEffect(() => {
    return () => {
      if (debouncedOnFilter) {
        debouncedOnFilter.cancel();
      }
    };
  }, [debouncedOnFilter]);
  useEffect(() => {
    setInputValues(currentInputValues => {
      const currentSerializedValue = serializeMultiChoiceFilterValues(currentInputValues);
      const nextInputValues = inputValuesFrom(filterValue);
      const nextSerializedValue = serializeMultiChoiceFilterValues(nextInputValues);
      if (currentSerializedValue === nextSerializedValue) {
        return currentInputValues;
      }
      return nextInputValues;
    });
  }, [filterValue]);
  const notifyFilterChange = updatedInputValues => {
    const serializedValue = serializeMultiChoiceFilterValues(updatedInputValues);
    if (onChange) {
      onChange(serializedValue);
    }
    if (debouncedOnFilter) {
      debouncedOnFilter(serializedValue);
    }
  };
  const updateInputValue = (index, nextValue) => {
    if (type === 'select' && nextValue === '') {
      setInputValues(['']);
      notifyFilterChange(['']);
      return;
    }
    const updatedInputValues = inputValues.map((inputValue, inputValueIndex) => inputValueIndex === index ? nextValue : inputValue);
    setInputValues(updatedInputValues);
    notifyFilterChange(updatedInputValues);
  };
  const addInputValueAfter = index => {
    const updatedInputValues = [...inputValues];
    updatedInputValues.splice(index + 1, 0, '');
    setInputValues(updatedInputValues);
  };
  const removeInputValue = index => {
    const updatedInputValues = inputValues.filter((_, inputValueIndex) => inputValueIndex !== index);
    const nextInputValues = updatedInputValues.length > 0 ? updatedInputValues : [''];
    setInputValues(nextInputValues);
    notifyFilterChange(nextInputValues);
  };
  const stopClickPropagation = event => {
    event.preventDefault();
    event.stopPropagation();
  };
  const stopPropagation = event => {
    event.stopPropagation();
  };
  const addInputValue = (event, index) => {
    stopClickPropagation(event);
    addInputValueAfter(index);
  };
  const deleteInputValue = (event, index) => {
    stopClickPropagation(event);
    removeInputValue(index);
  };
  const dataTestIdFor = index => {
    if (!dataTestId) return undefined;
    return index === 0 ? dataTestId : "".concat(dataTestId, "-").concat(index);
  };
  const addButtonDataTestIdFor = index => {
    if (!dataTestId) return undefined;
    return "".concat(dataTestId, "-add-").concat(index);
  };
  const removeButtonDataTestIdFor = index => {
    if (!dataTestId) return undefined;
    return "".concat(dataTestId, "-remove-").concat(index);
  };
  const renderControl = (inputValue, index) => {
    const appliedDropdownStyle = _objectSpread(_objectSpread({}, dropdownStyle), SHOULD_DEFINE_FILTER_MIN_WIDTH ? {
      minWidth: FILTER_CONTROL_MIN_WIDTH
    } : {});
    const appliedInputStyle = _objectSpread(_objectSpread({}, inputStyle), SHOULD_DEFINE_FILTER_MIN_WIDTH ? {
      minWidth: FILTER_CONTROL_MIN_WIDTH
    } : {});
    if (type === 'select') {
      return /*#__PURE__*/React.createElement(Dropdown, {
        dataTestId: dataTestIdFor(index),
        dropdownStyle: appliedDropdownStyle,
        onChange: nextValue => updateInputValue(index, nextValue),
        onClick: stopPropagation,
        options: selectOptionsWithAllOption(options),
        showNullOption: false,
        value: inputValue || ''
      });
    }
    return /*#__PURE__*/React.createElement(Form.Control, {
      className: inputClassName,
      "data-testid": dataTestIdFor(index),
      onChange: event => updateInputValue(index, event.target.value),
      onClick: stopClickPropagation,
      placeholder: placeholder,
      size: inputSize,
      style: appliedInputStyle,
      value: inputValue || ''
    });
  };
  const renderRemoveButtonSlot = index => {
    return /*#__PURE__*/React.createElement("span", {
      style: {
        minWidth: "".concat(REMOVE_BUTTON_SLOT_WIDTH, "px"),
        width: "".concat(REMOVE_BUTTON_SLOT_WIDTH, "px")
      }
    }, inputValues.length > 1 && /*#__PURE__*/React.createElement(DeleteButton, {
      "aria-label": "Remove OR filter value",
      "data-testid": removeButtonDataTestIdFor(index),
      onClick: event => deleteInputValue(event, index),
      type: "button"
    }));
  };
  return /*#__PURE__*/React.createElement("div", _extends({
    className: "MultiChoiceFilter d-flex flex-column gap-2 ".concat(className),
    style: _objectSpread(_objectSpread({}, style), {}, {
      borderLeft: "".concat(LEFT_BORDER_WIDTH, "px solid ").concat(shouldShowLeftBorder ? 'black' : 'transparent'),
      paddingLeft: "".concat(plusButtonLeftSpacing, "px")
    })
  }, props), inputValues.map((inputValue, index) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: "multi-choice-filter-".concat(index)
  }, index > 0 && /*#__PURE__*/React.createElement("div", {
    className: "text-muted font-weight-bold small",
    style: {
      width: "".concat(plusButtonColumnWidth, "px"),
      textAlign: 'center'
    }
  }, "OR"), /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center",
    style: {
      gap: "".concat(plusButtonInputGap, "px")
    }
  }, showPlusButton && /*#__PURE__*/React.createElement(Button, {
    "aria-label": "Add OR filter value",
    className: "p-0 d-inline-flex align-items-center justify-content-center rounded-circle",
    "data-testid": addButtonDataTestIdFor(index),
    icon: "add",
    onClick: event => addInputValue(event, index),
    style: {
      height: "".concat(ADD_BUTTON_SIZE, "px"),
      minWidth: "".concat(ADD_BUTTON_SIZE, "px"),
      width: "".concat(ADD_BUTTON_SIZE, "px")
    },
    type: "button",
    variant: "light"
  }), renderControl(inputValue, index), renderRemoveButtonSlot(index)))));
}